import { useClerk, useOrganization, useOrganizationList } from "@clerk/react";
import { PlusIcon, UploadIcon, UsersIcon } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { useCallback, useRef, useState, type ChangeEvent } from "react";

import { hasClerkPublishableKey } from "../../cloud/publicConfig";
import {
  OrganizationAvatar,
  PersonalAccountAvatar,
  useCompanyLogoFallback,
} from "../org/organizationShared";
import { OrganizationSettingsDisabledHint } from "../org/organizationMembersPanelView";
import { readCompanyLogoFile } from "../../onboardingStore";
import { useMorrowClerkAuth } from "../clerk/morrowClerkAuth";
import {
  organizationLogoUrl,
  resolveOrganizationLogoUrl,
} from "../onboarding/MorrowOnboarding.logic";
import { RenderErrorBoundary } from "../RenderErrorBoundary";
import { Button } from "../ui/button";
import { Select, SelectItem, SelectPopup, SelectTrigger, SelectValue } from "../ui/select";
import { Spinner } from "../ui/spinner";
import { useI18n } from "~/i18n";
import { SettingsRow, SettingsSection } from "./settingsLayout";

/**
 * Organization controls in our own Settings surface, mirroring
 * {@link AccountSettingsSection}. Shows the active organization (its real logo,
 * falling back to initials) or the personal account, and lets the user switch
 * between organizations, create a new one, and jump into member management —
 * all built from our own primitives instead of Clerk's floating switcher popup.
 *
 * Renders nothing on non-Clerk builds or while signed out, and swaps in a small
 * hint (via the error boundary) if Organizations are disabled on the Clerk
 * instance, so it is safe to drop into the shared General panel unconditionally.
 */
export function OrganizationSettingsSection() {
  if (!hasClerkPublishableKey()) return null;
  return <ConfiguredOrganizationSettingsSection />;
}

/** Personal-account sentinel for the switch <Select> (Clerk uses `null`). */
const PERSONAL_VALUE = "__personal__";

function ConfiguredOrganizationSettingsSection() {
  const { isSignedIn } = useMorrowClerkAuth();
  if (!isSignedIn) return null;
  return (
    <RenderErrorBoundary fallback={<OrganizationsDisabledSection />}>
      <OrganizationSettingsBody />
    </RenderErrorBoundary>
  );
}

function OrganizationSettingsBody() {
  const { t } = useI18n();
  const clerk = useClerk();
  const { isLoaded: orgLoaded, organization, membership } = useOrganization();
  const {
    isLoaded: listLoaded,
    setActive,
    userMemberships,
  } = useOrganizationList({
    userMemberships: true,
  });
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoOverrideUrl, setLogoOverrideUrl] = useState("");
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const companyLogoFallback = useCompanyLogoFallback();
  const isOrgAdmin = membership?.role === "org:admin";

  const handleLogoPick = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.currentTarget.files?.[0] ?? null;
      event.currentTarget.value = "";
      if (!file || !organization) return;
      setLogoUploading(true);
      try {
        await organization.setLogo({ file });
        const reloaded = await organization.reload();
        const uploaded = organizationLogoUrl(reloaded);
        if (uploaded) setLogoOverrideUrl(uploaded);
      } catch {
        const preview = await readCompanyLogoFile(file);
        if (preview) setLogoOverrideUrl(preview);
      } finally {
        setLogoUploading(false);
      }
    },
    [organization],
  );

  if (!orgLoaded) return null;

  const memberships = listLoaded ? (userMemberships.data ?? []) : [];
  const orgs = memberships.map((entry) => entry.organization);
  const activeName = organization?.name?.trim() || null;
  const activeLogoUrl =
    logoOverrideUrl || resolveOrganizationLogoUrl(organization, companyLogoFallback);
  const identity = activeName ?? t("settings.organization.personal");

  return (
    <SettingsSection id="organization" title={t("settings.section.organization")}>
      <SettingsRow
        title={
          <span className="flex items-center gap-2">
            {organization ? (
              <OrganizationAvatar logoUrl={activeLogoUrl} name={identity} />
            ) : (
              <PersonalAccountAvatar className="size-5" />
            )}
            <span className="truncate">{identity}</span>
          </span>
        }
        description={
          organization
            ? t("settings.organization.currentDescription")
            : t("settings.organization.personal")
        }
        control={
          organization ? (
            <Button size="xs" variant="outline" render={<Link to="/settings/organization" />}>
              <UsersIcon />
              {t("settings.organization.manageAction")}
            </Button>
          ) : undefined
        }
      />

      {organization && isOrgAdmin ? (
        <SettingsRow
          title={t("settings.organization.changeLogo")}
          description={t("settings.organization.changeLogoDescription")}
          control={
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="sr-only"
                onChange={(event) => void handleLogoPick(event)}
              />
              <Button
                size="xs"
                variant="outline"
                disabled={logoUploading}
                onClick={() => fileInputRef.current?.click()}
              >
                {logoUploading ? <Spinner /> : <UploadIcon />}
                {logoUploading
                  ? t("settings.organization.uploadingLogo")
                  : t("settings.organization.changeLogo")}
              </Button>
            </>
          }
        />
      ) : null}

      {orgs.length > 0 ? (
        <SettingsRow
          title={t("settings.organization.switchTitle")}
          description={t("settings.organization.switchDescription")}
          control={
            <Select
              value={organization?.id ?? PERSONAL_VALUE}
              onValueChange={(value) => {
                if (typeof value !== "string" || !setActive) return;
                void setActive({ organization: value === PERSONAL_VALUE ? null : value });
              }}
            >
              <SelectTrigger
                className="w-full sm:w-56"
                aria-label={t("settings.organization.switchTitle")}
              >
                <SelectValue>{identity}</SelectValue>
              </SelectTrigger>
              <SelectPopup align="end" alignItemWithTrigger={false}>
                <SelectItem hideIndicator value={PERSONAL_VALUE}>
                  <span className="flex items-center gap-2">
                    <PersonalAccountAvatar className="size-5" />
                    {t("settings.organization.personal")}
                  </span>
                </SelectItem>
                {orgs.map((org) => (
                  <SelectItem hideIndicator key={org.id} value={org.id}>
                    <span className="flex items-center gap-2">
                      <OrganizationAvatar
                        logoUrl={resolveOrganizationLogoUrl(org, companyLogoFallback)}
                        name={org.name}
                      />
                      <span className="truncate">{org.name}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectPopup>
            </Select>
          }
        />
      ) : null}

      <SettingsRow
        title={t("settings.organization.createTitle")}
        description={t("settings.organization.createDescription")}
        control={
          <Button size="xs" variant="outline" onClick={() => clerk.openCreateOrganization()}>
            <PlusIcon />
            {t("settings.organization.createAction")}
          </Button>
        }
      />
    </SettingsSection>
  );
}

function OrganizationsDisabledSection() {
  const { t } = useI18n();
  return (
    <SettingsSection id="organization" title={t("settings.section.organization")}>
      <div className="px-3 sm:px-4">
        <OrganizationSettingsDisabledHint>
          {t("settings.organization.disabled")}
        </OrganizationSettingsDisabledHint>
      </div>
    </SettingsSection>
  );
}
